import { useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

import {
  setPage,
  setTotal,
  setLimit,
  setColumnVisibility,
  setRoundTableFilters,
  setCurrentRoundConfig,
  setClearRoundTableFilters,
  setShowRoundStatusModalDialog,
} from '@Modal/reducers/rounds/table/minesRoundsTableSlice';

// Types
import { StoreInterface } from 'src/app/types';
import { FilterItemType } from '@Modal/reducers/types';
import { RoundConfigItemDto } from '@contracts/api/dto/providers/RoundConfigDto';

export const useMinesRoundsTable = () => {
  const dispatch = useDispatch();
  const {
    page,
    limit,
    total,
    selectedFilters,
    columnVisibility,
    roundTableFilters,
    currentRoundConfig,
    columnVisibilityOptions,
    showRoundStatusModalDialog,
  } = useSelector((state: StoreInterface) => state?.minesRoundTable);

  const roundsListFilters = useMemo(() => {
    const columnVisibilityStrArr = Array.from(Object.values(columnVisibility)).map((filt) => filt);
    return columnVisibilityStrArr as string[];
  }, [columnVisibility]);

  const handleApplyColumnVisibility = (column: string[]) => {
    const newVisibleColumns = [...roundsListFilters, ...column];
    if (roundsListFilters.filter((col) => column.includes(col)).length > 0) {
      const filteredNewVisibleColumns = roundsListFilters.filter((col) => col !== column[0]);
      dispatch(setColumnVisibility(filteredNewVisibleColumns));
    } else {
      dispatch(setColumnVisibility(newVisibleColumns));
    }
  };

  const handleFilterSelect = (filter: FilterItemType) => {
    dispatch(setRoundTableFilters(filter));
  };

  const visiblityCustomizable = useMemo(
    () => ({
      value: columnVisibility,
      options: columnVisibilityOptions,
      apply: (value: string[]) => handleApplyColumnVisibility(value),
    }),
    [columnVisibility, columnVisibilityOptions]
  );

  const clearRoundTableFiltersHandler = () => {
    dispatch(setClearRoundTableFilters());
  };

  const handleSetCurrentRoundConfig = (arg: RoundConfigItemDto) => {
    dispatch(setCurrentRoundConfig(arg));
  };

  return {
    // State
    limit,
    page,
    total,
    selectedFilters,
    columnVisibility,
    roundTableFilters,
    roundsListFilters,
    currentRoundConfig,
    visiblityCustomizable,
    columnVisibilityOptions,
    showRoundStatusModalDialog,
    // Actions
    setPage,
    setLimit,
    setTotal,
    setRoundTableFilters,
    setCurrentRoundConfig,
    setClearRoundTableFilters,
    setShowRoundStatusModalDialog,
    // Handlers
    handleFilterSelect,
    handleSetCurrentRoundConfig,
    handleApplyColumnVisibility,
    clearRoundTableFiltersHandler,
  };
};
